import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Save, RefreshCw, BookMarked, Download, FileText, ChevronDown, ChevronUp, Clock, User, History, Undo2, X, Upload, AlertTriangle, CheckCircle2 } from 'lucide-react';

const COLOURS = ['D','E','F','G','H','I','J','K','L','M','N','O','P'];
const CLARITIES = ['VVS','VS','SI','I'];

export default function DiamondProportions() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [shapes, setShapes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [selectedShape, setSelectedShape] = useState('Round');
  const [selectedSize, setSelectedSize] = useState('0-6');
  // proportions[colour][clarity] = pct value (string for editing)
  const [proportions, setProportions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // History
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyNote, setHistoryNote]             = useState('');
  const [savingHistory, setSavingHistory]         = useState(false);
  const [history, setHistory]                     = useState([]);

  // Proportion-only Snapshots (Phase H — for rollback after bulk imports)
  const [showSnapshotsDialog, setShowSnapshotsDialog] = useState(false);
  const [propSnapshots, setPropSnapshots]             = useState([]);
  const [snapshotsLoading, setSnapshotsLoading]       = useState(false);
  const [snapshotReason, setSnapshotReason]           = useState('');
  const [creatingSnapshot, setCreatingSnapshot]       = useState(false);
  const [confirmRestoreId, setConfirmRestoreId]       = useState(null);
  const [restoring, setRestoring]                     = useState(false);

  // PDF Import (Phase H.2 — drag-and-drop vendor PDF, preview diff, apply)
  const [importPreview, setImportPreview]   = useState(null);  // server response
  const [importingPdf, setImportingPdf]     = useState(false);
  const [applyingImport, setApplyingImport] = useState(false);
  const [importFile, setImportFile]         = useState(null);   // File object
  const [historyExpanded, setHistoryExpanded]     = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadShapesAndSizes();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (selectedShape && selectedSize) loadProportions();
  }, [selectedShape, selectedSize]);

  const loadShapesAndSizes = async () => {
    try {
      const [sRes, szRes] = await Promise.all([
        api.get('/catalog/diamond-shapes'),
        api.get('/catalog/diamond-sizes'),
      ]);
      const shapeNames = sRes.data.map(s => s.name);
      const sizeNames = szRes.data.map(s => s.name);
      setShapes(shapeNames);
      setSizes(sizeNames);
      if (shapeNames[0]) setSelectedShape(shapeNames[0]);
      if (sizeNames[0]) setSelectedSize(sizeNames[0]);
    } catch {
      toast.error('Failed to load shapes/sizes');
    }
  };

  const loadProportions = async () => {
    setLoading(true);
    setDirty(false);
    try {
      const res = await api.get('/diamond-proportions', {
        params: { shape: selectedShape, size: selectedSize },
      });
      const map = {};
      for (const colour of COLOURS) {
        map[colour] = {};
        for (const clarity of CLARITIES) {
          map[colour][clarity] = '';
        }
      }
      for (const doc of res.data) {
        if (!map[doc.colour]) map[doc.colour] = {};
        map[doc.colour][doc.clarity] = String(doc.proportion_pct);
      }
      setProportions(map);
    } catch {
      toast.error('Failed to load proportions');
    } finally {
      setLoading(false);
    }
  };

  const handleCellChange = (colour, clarity, val) => {
    setProportions(prev => ({
      ...prev,
      [colour]: { ...prev[colour], [clarity]: val },
    }));
    setDirty(true);
  };

  const handleSaveAll = async () => {
    if (!isAdmin) return;
    setSaving(true);
    let successCount = 0;
    let errorCount = 0;
    try {
      const promises = [];
      for (const colour of COLOURS) {
        for (const clarity of CLARITIES) {
          const val = proportions[colour]?.[clarity];
          if (val === '' || val == null) continue;
          const pct = parseFloat(val);
          if (isNaN(pct) || pct < 0) { errorCount++; continue; }
          promises.push(
            api.put('/diamond-proportions', {
              shape: selectedShape,
              size: selectedSize,
              colour,
              clarity,
              proportion_pct: pct,
            }).then(() => { successCount++; }).catch(() => { errorCount++; })
          );
        }
      }
      await Promise.all(promises);
      if (errorCount > 0) toast.warning(`Saved ${successCount}, ${errorCount} failed`);
      else toast.success(`Proportions saved — ${successCount} combinations updated`);
      setDirty(false);
      await loadProportions();
    } catch {
      toast.error('Failed to save proportions');
    } finally {
      setSaving(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/diamond-price-history');
      setHistory(res.data);
    } catch (e) { /* silently ignore */ }
  };

  const handleSaveToHistory = async () => {
    setSavingHistory(true);
    try {
      const res = await api.post('/diamond-price-history', {
        source: 'proportions',
        note: historyNote.trim(),
      });
      toast.success(`Snapshot saved — ${res.data.total_prices} prices archived${res.data.pdf_url ? ' · PDF ready' : ''}`);
      setShowHistoryDialog(false);
      setHistoryNote('');
      setHistoryExpanded(true);
      await fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save history');
    } finally { setSavingHistory(false); }
  };

  const fmtDate = str => {
    if (!str) return '';
    const d = new Date(str.endsWith('Z') ? str : str + 'Z');
    return d.toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  // ── Proportion-only Snapshots (Phase H) ──────────────────────────────────
  // Lightweight backup/restore of just the 832-cell proportion grid.
  // Use this before bulk imports (e.g. PDF reprice) so you can roll back.
  const fetchPropSnapshots = useCallback(async () => {
    setSnapshotsLoading(true);
    try {
      const res = await api.get('/diamond-proportions/history');
      setPropSnapshots(res.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load snapshots');
    } finally { setSnapshotsLoading(false); }
  }, []);

  const openSnapshotsDialog = () => {
    setShowSnapshotsDialog(true);
    fetchPropSnapshots();
  };

  const createPropSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      await api.post('/diamond-proportions/history/snapshot', {
        reason: snapshotReason.trim() || 'Manual snapshot',
      });
      toast.success('Snapshot captured');
      setSnapshotReason('');
      fetchPropSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Snapshot failed');
    } finally { setCreatingSnapshot(false); }
  };

  const restorePropSnapshot = async (id) => {
    setRestoring(true);
    try {
      const res = await api.post(`/diamond-proportions/history/${id}/restore`);
      toast.success(`Restored ${res.data.restored_cells} cells — auto-snapshot of previous state created`);
      setConfirmRestoreId(null);
      fetchPropSnapshots();
      // Reload current grid
      loadProportions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally { setRestoring(false); }
  };

  const deletePropSnapshot = async (id) => {
    try {
      await api.delete(`/diamond-proportions/history/${id}`);
      toast.success('Snapshot deleted');
      fetchPropSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // PDF Import — preview (does NOT touch DB)
  const handlePdfPreview = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please pick a .pdf file');
      return;
    }
    setImportingPdf(true);
    setImportFile(file);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const res = await api.post('/diamond-proportions/import-pdf', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF parse failed');
      setImportFile(null);
    } finally { setImportingPdf(false); }
  };

  // PDF Import — apply (creates auto-snapshot first, then writes all cells)
  const handlePdfApply = async () => {
    if (!importFile) return;
    setApplyingImport(true);
    try {
      const fd = new FormData();
      fd.append('pdf', importFile);
      const res = await api.post('/diamond-proportions/import-pdf?commit=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(
        `Imported — ${res.data.summary.changed} cells updated · snapshot saved`
      );
      setImportPreview(null);
      setImportFile(null);
      // Refresh grid + snapshot list
      loadProportions();
      fetchPropSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setApplyingImport(false); }
  };

  const cancelImport = () => {
    setImportPreview(null);
    setImportFile(null);
  };

  const sourceDot = s => s === 'proportions' ? 'bg-violet-400' : 'bg-emerald-500';
  const sourceLabel = s => s === 'proportions' ? 'Proportion change' : 'Index change';

  const clarityColor = { VVS: 'text-blue-700', VS: 'text-indigo-600', SI: 'text-violet-600', I: 'text-gray-500' };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white p-3 sticky top-0 z-10 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-[#128C7E] flex-shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-semibold leading-tight truncate">Price Proportions</h1>
            <p className="hidden md:block text-xs text-emerald-200 truncate">Proportion % per Shape · Size · Colour · Clarity</p>
          </div>
          {isAdmin && dirty && (
            <Button
              data-testid="save-proportions-btn"
              onClick={handleSaveAll}
              disabled={saving}
              size="sm"
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white flex-shrink-0"
            >
              <Save className="h-4 w-4 md:mr-1.5" />
              <span className="hidden md:inline">{saving ? 'Saving...' : 'Save Changes'}</span>
            </Button>
          )}
          {isAdmin && !dirty && (
            <label
              className="cursor-pointer border border-amber-300/60 text-amber-100 hover:bg-amber-500/10 flex items-center gap-1.5 px-2 md:px-3 h-9 rounded-md text-sm font-medium flex-shrink-0"
              data-testid="proportions-import-pdf-label"
              title="Import PDF"
            >
              <Upload className="h-4 w-4" />
              <span className="hidden md:inline">Import PDF</span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) handlePdfPreview(f);
                  e.target.value = '';
                }}
                disabled={importingPdf}
                data-testid="proportions-import-pdf-input"
              />
            </label>
          )}
          {isAdmin && !dirty && (
            <Button
              data-testid="proportions-snapshots-btn"
              variant="outline"
              size="sm"
              onClick={openSnapshotsDialog}
              title="Snapshots"
              className="border-violet-300/60 text-violet-100 hover:bg-violet-500/10 flex-shrink-0 gap-1.5 px-2 md:px-3"
            >
              <History className="h-4 w-4" />
              <span className="hidden md:inline">Snapshots</span>
            </Button>
          )}
          {isAdmin && !dirty && (
            <Button
              data-testid="proportions-save-to-history-btn"
              variant="outline"
              size="sm"
              onClick={() => setShowHistoryDialog(true)}
              title="Save to History"
              className="border-white/40 text-white hover:bg-white/10 flex-shrink-0 gap-1.5 px-2 md:px-3"
            >
              <BookMarked className="h-4 w-4" />
              <span className="hidden md:inline">Save to History</span>
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-4">

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Shape</label>
              <div className="flex gap-2 flex-wrap">
                {shapes.map(shape => (
                  <button
                    key={shape}
                    data-testid={`shape-filter-${shape.toLowerCase()}`}
                    onClick={() => setSelectedShape(shape)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      selectedShape === shape
                        ? 'bg-[#075E54] text-white border-[#075E54]'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-[#075E54]'
                    }`}
                  >
                    {shape}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Size Range</label>
              <div className="flex gap-2 flex-wrap">
                {sizes.map(size => (
                  <button
                    key={size}
                    data-testid={`size-filter-${size}`}
                    onClick={() => setSelectedSize(size)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selectedSize === size
                        ? 'bg-[#128C7E] text-white border-[#128C7E]'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-[#128C7E]'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadProportions}
              disabled={loading}
              className="text-gray-500 hover:text-[#075E54]"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Showing: <strong className="text-gray-600">{selectedShape}</strong> · size <strong className="text-gray-600">{selectedSize}</strong> &nbsp;·&nbsp; {COLOURS.length} colours × {CLARITIES.length} clarities = {COLOURS.length * CLARITIES.length} cells
            </p>
            {isAdmin && dirty && (
              <p className="text-xs text-amber-600 font-medium">Unsaved changes — click Save Changes above</p>
            )}
          </div>
        </div>

        {/* Matrix Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Loading proportions...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="proportions-table">
                <thead>
                  <tr className="bg-[#075E54]/5 border-b">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 w-16 border-r border-gray-100">
                      <span className="text-xs">Colour</span>
                    </th>
                    {CLARITIES.map(c => (
                      <th key={c} className="text-center px-4 py-3 font-semibold border-r border-gray-100 last:border-r-0">
                        <span className={`text-sm ${clarityColor[c]}`}>{c}</span>
                        <div className="text-[10px] text-gray-400 font-normal mt-0.5">% proportion</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COLOURS.map((colour, i) => (
                    <tr key={colour} className={`border-b last:border-b-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                      <td className="px-4 py-2.5 border-r border-gray-100">
                        <span className="font-bold text-[#075E54] text-base">{colour}</span>
                      </td>
                      {CLARITIES.map(clarity => (
                        <td key={clarity} className="px-2 py-2 border-r border-gray-100 last:border-r-0">
                          {isAdmin ? (
                            <Input
                              data-testid={`prop-${colour}-${clarity}`}
                              type="number"
                              min="0"
                              max="500"
                              step="0.001"
                              value={proportions[colour]?.[clarity] ?? ''}
                              onChange={e => handleCellChange(colour, clarity, e.target.value)}
                              className="h-8 text-center font-mono text-xs w-28 border-gray-200 focus:border-[#075E54]"
                              placeholder="0.000"
                            />
                          ) : (
                            <span className="font-mono text-xs text-gray-700 block text-center">
                              {proportions[colour]?.[clarity]
                                ? `${Number(proportions[colour][clarity]).toFixed(3)}%`
                                : <span className="text-gray-300">—</span>}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs text-amber-700">
            <strong>How it works:</strong> The proportion % value for each combination of Shape + Size + Colour + Clarity is multiplied by the Diamond Index to compute the final price per unit.
            &nbsp; Example: Diamond Index ₹78,000 × 141.026% ÷ 100 = <strong>₹1,10,000</strong>
          </p>
          <button
            className="mt-2 text-xs text-[#075E54] font-medium hover:underline"
            onClick={() => navigate('/diamond-index')}
          >
            Set or update Diamond Index →
          </button>
        </div>

        {/* History section */}
        {history.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <button
              data-testid="toggle-proportions-history-btn"
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              onClick={() => setHistoryExpanded(v => !v)}
            >
              <div className="flex items-center gap-2">
                <BookMarked className="h-4 w-4 text-[#075E54]" />
                <span className="text-sm font-semibold text-gray-800">Price History</span>
                <span className="text-xs bg-[#075E54]/10 text-[#075E54] font-medium px-2 py-0.5 rounded-full">
                  {history.length} snapshot{history.length !== 1 ? 's' : ''}
                </span>
              </div>
              {historyExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>

            {historyExpanded && (
              <div className="border-t border-gray-100">
                {history.map((item, idx) => (
                  <div
                    key={item.id}
                    className={`flex items-start justify-between gap-3 p-4 ${idx < history.length - 1 ? 'border-b border-gray-100' : ''} hover:bg-gray-50/50`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${sourceDot(item.source)}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-[#075E54]">
                            {item.index_value ? `₹${Number(item.index_value).toLocaleString('en-IN')}` : '—'}
                          </span>
                          {item.note && (
                            <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full truncate max-w-[160px]">
                              {item.note}
                            </span>
                          )}
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{sourceLabel(item.source)}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(item.saved_at)}</span>
                          <span className="flex items-center gap-1"><User className="h-3 w-3" />{item.saved_by}</span>
                          <span className="text-gray-400">{item.total_prices} prices</span>
                        </p>
                      </div>
                    </div>
                    {item.pdf_url && (
                      <a
                        href={`${API_BASE}${item.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={`DiaForge_PriceBook_${item.saved_at?.substring(0, 10)}.pdf`}
                        className="flex-shrink-0 flex items-center gap-1.5 text-xs text-[#075E54] border border-[#075E54]/30 hover:bg-[#075E54]/5 px-2.5 py-1.5 rounded-lg transition-colors font-medium"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        PDF
                        <Download className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Save to History Dialog */}
      {showHistoryDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] p-5 text-white">
              <div className="flex items-center gap-2 mb-1">
                <BookMarked className="h-5 w-5 text-emerald-200" />
                <h2 className="text-base font-bold">Save to History</h2>
              </div>
              <p className="text-xs text-emerald-200">Snapshot of current proportions + computed prices + PDF</p>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">What&apos;s captured</span>
                  <span className="font-medium text-gray-700">All 832 proportions + prices</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Triggered by</span>
                  <span className="font-medium text-violet-700">Proportion change</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">PDF</span>
                  <span className="font-medium text-gray-700">Auto-generated & stored</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Label / Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <Input
                  data-testid="proportions-history-note-input"
                  placeholder="e.g. Q1 2026 rate revision..."
                  value={historyNote}
                  onChange={e => setHistoryNote(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveToHistory()}
                  autoFocus
                />
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-3">
              <Button
                variant="outline" className="flex-1"
                onClick={() => { setShowHistoryDialog(false); setHistoryNote(''); }}
                disabled={savingHistory}
              >Cancel</Button>
              <Button
                data-testid="confirm-proportions-history-btn"
                className="flex-1 bg-[#075E54] hover:bg-[#128C7E] text-white gap-2"
                onClick={handleSaveToHistory}
                disabled={savingHistory}
              >
                <BookMarked className="h-4 w-4" />
                {savingHistory ? 'Generating PDF...' : 'Save to History'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Proportion-only Snapshots dialog (Phase H — rollback support) */}
      {showSnapshotsDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl p-5 max-w-xl w-full max-h-[92vh] overflow-y-auto" data-testid="proportions-snapshots-dialog">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                  <History className="w-5 h-5 text-violet-700" /> Proportion Snapshots
                </h3>
                <p className="text-xs text-slate-500">
                  Backups of the entire 832-cell proportion grid. Use before
                  bulk imports so you can roll back instantly.
                </p>
              </div>
              <button onClick={() => setShowSnapshotsDialog(false)}
                className="p-1.5 hover:bg-slate-100 rounded-lg"
                data-testid="proportions-snapshots-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Capture a fresh snapshot */}
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-4">
              <label className="text-[10px] uppercase font-bold text-violet-700 tracking-wide">
                Capture new snapshot
              </label>
              <div className="flex gap-2 mt-1">
                <input
                  type="text"
                  value={snapshotReason}
                  onChange={e => setSnapshotReason(e.target.value)}
                  placeholder="e.g. Before importing July rates PDF"
                  className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="snapshot-reason-input"
                />
                <Button onClick={createPropSnapshot} disabled={creatingSnapshot}
                  className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
                  data-testid="snapshot-create-btn">
                  <BookMarked className="w-4 h-4" />
                  {creatingSnapshot ? 'Saving…' : 'Snapshot'}
                </Button>
              </div>
            </div>

            {/* Snapshot list */}
            <div className="space-y-2" data-testid="snapshots-list">
              {snapshotsLoading ? (
                <div className="text-center text-slate-500 py-6 text-sm">Loading…</div>
              ) : propSnapshots.length === 0 ? (
                <div className="text-center text-slate-500 py-6 text-sm">No snapshots yet.</div>
              ) : propSnapshots.map(s => (
                <div key={s.id}
                  className="border border-slate-200 rounded-lg p-3 bg-white flex items-start gap-3"
                  data-testid={`snapshot-row-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 text-sm">
                        {s.snapshot_reason || '(no reason)'}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {s.cell_count} cells
                      </span>
                      {s.source_pdf && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                          PDF: {s.source_pdf}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
                      {fmtDate(s.snapshot_at)}
                      {s.created_by && <> · <User className="w-3 h-3 inline -mt-0.5" /> {s.created_by}</>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <Button size="sm" variant="outline"
                      onClick={() => setConfirmRestoreId(s.id)}
                      className="border-violet-500/40 text-violet-700 hover:bg-violet-500/5 text-xs h-7"
                      data-testid={`snapshot-restore-${s.id}`}>
                      <Undo2 className="w-3 h-3 mr-1" /> Restore
                    </Button>
                    <Button size="sm" variant="ghost"
                      onClick={() => deletePropSnapshot(s.id)}
                      className="text-rose-600 hover:bg-rose-50 text-xs h-7"
                      data-testid={`snapshot-delete-${s.id}`}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Restore confirmation */}
      {confirmRestoreId && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full" data-testid="restore-confirm">
            <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-violet-700" /> Restore this snapshot?
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              This will overwrite all 832 proportion cells with the snapshot
              values. The current state will be auto-snapshotted first so
              you can roll back.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmRestoreId(null)}
                className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium"
                data-testid="restore-cancel">Cancel</button>
              <button onClick={() => restorePropSnapshot(confirmRestoreId)}
                disabled={restoring}
                className="px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-50"
                data-testid="restore-confirm-btn">
                {restoring ? 'Restoring…' : 'Yes, restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Import — busy spinner during parse */}
      {importingPdf && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" data-testid="pdf-importing">
            <Upload className="w-10 h-10 mx-auto text-amber-600 animate-pulse mb-3" />
            <h3 className="font-bold text-slate-900">Parsing PDF…</h3>
            <p className="text-sm text-slate-500 mt-1">Reading {importFile?.name}</p>
          </div>
        </div>
      )}

      {/* PDF Import — preview & apply */}
      {importPreview && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl p-5 max-w-3xl w-full max-h-[92vh] overflow-y-auto" data-testid="pdf-import-preview">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-600" /> PDF Import Preview
                </h3>
                <p className="text-xs text-slate-500">{importPreview.filename}</p>
              </div>
              <button onClick={cancelImport}
                className="p-1.5 hover:bg-slate-100 rounded-lg"
                data-testid="pdf-import-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <SummaryTile label="Parsed" value={importPreview.summary.pdf_cells} color="indigo" />
              <SummaryTile label="Unchanged" value={importPreview.summary.matched} color="slate" />
              <SummaryTile label="Will change" value={importPreview.summary.changed} color="amber" />
              <SummaryTile label="New cells" value={importPreview.summary.added} color="emerald" />
            </div>

            {/* Validations */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-200">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">
                Computation-logic checks
              </h4>
              <div className="space-y-1.5" data-testid="pdf-import-validations">
                {importPreview.validations.map((v, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      {v.passed
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                      <span className="text-slate-700">{v.name}</span>
                    </div>
                    <span className={v.passed ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>
                      {v.passed ? 'OK' : `${v.count} issue${v.count === 1 ? '' : 's'}`}
                    </span>
                  </div>
                ))}
              </div>
              {/* Expand violations */}
              {importPreview.validations.some(v => !v.passed) && (
                <details className="mt-2">
                  <summary className="text-xs text-slate-600 cursor-pointer hover:text-slate-900">
                    Show {importPreview.validations.reduce((s, v) => s + (v.passed ? 0 : v.count), 0)} violation(s)
                  </summary>
                  <div className="mt-2 space-y-2 text-[11px] text-slate-600 max-h-32 overflow-y-auto">
                    {importPreview.validations.filter(v => !v.passed).map((v, i) => (
                      <div key={i}>
                        <div className="font-bold text-slate-700">{v.name}</div>
                        {v.violations.slice(0, 10).map((line, j) => (
                          <div key={j} className="pl-3 font-mono">{line}</div>
                        ))}
                        {v.violations.length > 10 && (
                          <div className="pl-3 italic text-slate-500">… and {v.violations.length - 10} more</div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>

            {/* Changes by block */}
            <div className="mb-4">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">
                Changes ({Object.keys(importPreview.changes_by_block || {}).length} block{Object.keys(importPreview.changes_by_block || {}).length === 1 ? '' : 's'})
              </h4>
              {Object.keys(importPreview.changes_by_block || {}).length === 0 ? (
                <div className="text-xs text-slate-500 italic">
                  No changes — the PDF matches the current DB exactly. Nothing to apply.
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="pdf-import-changes">
                  {Object.entries(importPreview.changes_by_block).map(([block, rows]) => (
                    <details key={block} className="border border-slate-200 rounded-md bg-white">
                      <summary className="px-3 py-2 cursor-pointer text-sm font-bold text-slate-800 hover:bg-slate-50 flex items-center justify-between">
                        <span>{block}</span>
                        <span className="text-xs font-normal text-slate-500">{rows.length} cells</span>
                      </summary>
                      <div className="px-3 pb-2 text-xs font-mono">
                        <div className="grid grid-cols-4 gap-2 text-slate-400 border-b py-1">
                          <span>Cell</span>
                          <span className="text-right">From</span>
                          <span className="text-right">To</span>
                          <span className="text-right">Δ</span>
                        </div>
                        {rows.map((r, i) => (
                          <div key={i} className="grid grid-cols-4 gap-2 py-0.5 hover:bg-slate-50">
                            <span className="text-slate-700">{r.colour}-{r.clarity}</span>
                            <span className="text-right text-slate-500">{r.from.toFixed(3)}</span>
                            <span className="text-right text-slate-800 font-bold">{r.to.toFixed(3)}</span>
                            <span className={`text-right font-bold ${r.delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {r.delta > 0 ? '+' : ''}{r.delta.toFixed(3)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={cancelImport} disabled={applyingImport}
                data-testid="pdf-import-cancel">
                Cancel
              </Button>
              <Button
                onClick={handlePdfApply}
                disabled={applyingImport || importPreview.summary.changed === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                data-testid="pdf-import-apply"
              >
                <CheckCircle2 className="w-4 h-4" />
                {applyingImport ? 'Applying…' : `Apply (${importPreview.summary.changed} cells)`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function SummaryTile({ label, value, color }) {
  const map = {
    indigo:  'bg-indigo-50 text-indigo-700 border-indigo-100',
    slate:   'bg-slate-50 text-slate-700 border-slate-200',
    amber:   'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  };
  return (
    <div className={`rounded-lg border p-2 text-center ${map[color] || map.slate}`}>
      <div className="text-[10px] font-bold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}
